import { useEffect, useState } from "react";
import { createDraft, listProducts, loadDefaultTax, type FxProduct } from "./facturx";
import { Modal } from "./Modal";

export function PieceSheet({
  companyId,
  clientId,
  docType,
  onClose,
  onCreated,
}: {
  companyId: number;
  clientId: number;
  docType: "quote" | "invoice";
  onClose: () => void;
  onCreated: () => void;
}) {
  const [products, setProducts] = useState<FxProduct[]>([]);
  const [taxId, setTaxId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [productId, setProductId] = useState<number | null>(null);
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    void listProducts(companyId)
      .then((data) => {
        if (alive) setProducts(data.products || []);
      })
      .catch(() => {
        if (alive) setProducts([]);
      });
    void loadDefaultTax(companyId)
      .then((data) => {
        if (alive) setTaxId(data.default_tax_rate_id ?? null);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [companyId]);

  const hits = query.trim()
    ? products.filter((item) => item.label.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8)
    : [];
  const title = docType === "quote" ? "Nouveau devis" : "Nouvelle facture";

  function pick(item: FxProduct) {
    setProductId(item.id);
    setQuery(item.label);
    setQty(String(item.default_qty || 1).replace(".", ","));
    setPrice(centsToInput(item.unit_price_ht_cents));
    if (item.tax_rate_id) setTaxId(item.tax_rate_id);
  }

  async function submit(close: () => void) {
    const label = query.trim();
    const quantity = Number(qty.replace(",", "."));
    const cents = inputToCents(price);
    if (!label || !Number.isFinite(quantity) || quantity <= 0 || cents === null) {
      setError("Libellé, quantité et montant HT sont obligatoires.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await createDraft({
        company_id: companyId,
        client_id: clientId,
        doc_type: docType,
        lines: [
          {
            product_id: productId,
            label,
            quantity,
            unit_price_ht_cents: cents,
            tax_rate_id: taxId,
          },
        ],
      });
      onCreated();
      close();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Le brouillon n’a pas été créé.");
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit(close);
          }}
        >
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">Brouillon seulement. Rien n’est validé.</p>
          <label className="mt-4 block text-sm font-medium">
            Article ou libellé libre
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setProductId(null);
              }}
              className="crm-field mt-1 w-full rounded-xl border px-3 py-2"
              autoFocus
            />
          </label>
          {hits.length > 0 && productId === null ? (
            <ul className="mt-2 overflow-hidden rounded-xl border border-slate-200">
              {hits.map((item) => (
                <li key={item.id}>
                  <button type="button" className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-black/5" onClick={() => pick(item)}>
                    <span className="truncate">{item.label}</span>
                    <span className="shrink-0 text-slate-500">{centsToInput(item.unit_price_ht_cents)} €</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="text-sm font-medium">
              Quantité
              <input value={qty} onChange={(event) => setQty(event.target.value)} className="crm-field mt-1 w-full rounded-xl border px-3 py-2" inputMode="decimal" />
            </label>
            <label className="text-sm font-medium">
              Montant HT
              <input value={price} onChange={(event) => setPrice(event.target.value)} placeholder="0,00" className="crm-field mt-1 w-full rounded-xl border px-3 py-2" inputMode="decimal" />
            </label>
          </div>
          {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="crm-pill rounded-full px-4 py-2 text-sm font-semibold" onClick={close} disabled={busy}>
              Annuler
            </button>
            <button type="submit" className="crm-fill rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50" disabled={busy}>
              {busy ? "Envoi…" : "Créer le brouillon"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function centsToInput(cents: number): string {
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function inputToCents(raw: string): number | null {
  const text = raw.trim().replace(/[\s\u00a0\u202f]/g, "").replace(",", ".");
  if (text === "") return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}
